/**
 * Helpers for dual-unit non-fuel purchase quantity (bottles/cans/barrels + litres/kg).
 * Stored purchase quantity remains bottles/cans/packs/barrels.
 * Pack-size rules come from the backend (purchase_qty_meta / get_nf_pp_price) — do not re-derive.
 */

export const NF_PACK_QTY_TOLERANCE = 0.001;

export function emptyNfPurchaseQtyMeta() {
  return {
    supports_dual_qty: false,
    pack_size: 0,
    purchase_unit_label: 'Pieces',
    pack_volume_unit: 'L',
    pack_size_hint: '',
  };
}

export function nfPurchaseQtyMetaFromApi(row) {
  if (!row || typeof row !== 'object') {
    return emptyNfPurchaseQtyMeta();
  }

  return {
    supports_dual_qty: Boolean(row.supports_dual_qty),
    pack_size: Number(row.pack_size) > 0 ? Number(row.pack_size) : 0,
    purchase_unit_label: row.purchase_unit_label || 'Pieces',
    pack_volume_unit: row.pack_volume_unit || 'L',
    pack_size_hint: row.pack_size_hint || '',
  };
}

export function litresFromBottles(bottles, packSize) {
  const packs = Number(bottles);
  const size = Number(packSize);
  if (!Number.isFinite(packs) || !Number.isFinite(size) || size <= 0) {
    return '';
  }
  return (packs * size).toFixed(3);
}

export function bottlesFromLitres(litres, packSize) {
  const vol = Number(litres);
  const size = Number(packSize);
  if (!Number.isFinite(vol) || !Number.isFinite(size) || size <= 0) {
    return '';
  }
  return (vol / size).toFixed(3);
}

/** True when litres ÷ pack size is a whole number of packs (within tolerance). */
export function isWholePackFromLitres(litres, packSize) {
  const vol = Number(litres);
  const size = Number(packSize);
  if (!Number.isFinite(vol) || !Number.isFinite(size) || size <= 0) {
    return false;
  }
  const packs = vol / size;
  return Math.abs(packs - Math.round(packs)) <= NF_PACK_QTY_TOLERANCE;
}

export function isWholePackQuantity(quantity) {
  const qty = Number(quantity);
  if (!Number.isFinite(qty)) {
    return false;
  }
  return Math.abs(qty - Math.round(qty)) <= NF_PACK_QTY_TOLERANCE;
}

export function dualQtyLitresMismatchMessage(meta) {
  const size = Number(meta?.pack_size) > 0 ? Number(meta.pack_size) : 0;
  const volumeUnit = meta?.pack_volume_unit || 'L';
  const label = String(meta?.purchase_unit_label || '');
  const unitNoun = /barrel/i.test(label)
    ? 'barrel'
    : (/bottle/i.test(label) ? 'bottle/can' : 'pack');
  const sizeText = size
    ? String(size).replace(/\.?0+$/, '')
    : '';
  return `Litres must be a multiple of ${sizeText} ${volumeUnit} (one ${unitNoun})`;
}

function packNoun(meta, qty) {
  const label = String(meta?.purchase_unit_label || '');
  const absQty = Math.abs(Number(qty));
  const singular = absQty === 1;
  if (/barrel/i.test(label)) {
    return singular ? 'barrel' : 'barrels';
  }
  if (/bottle/i.test(label)) {
    return singular ? 'bottle' : 'bottles';
  }
  return singular ? 'pack' : 'packs';
}

export function formatNfPurchaseQtyDisplay(quantity, meta) {
  const qty = Number(quantity);
  const qtyText = Number.isFinite(qty)
    ? qty.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })
    : String(quantity ?? '');

  if (!meta?.supports_dual_qty || !(Number(meta.pack_size) > 0) || !Number.isFinite(qty)) {
    const label = meta?.purchase_unit_label ? ` ${meta.purchase_unit_label}` : '';
    return `${qtyText}${label}`;
  }

  const litres = litresFromBottles(qty, meta.pack_size);
  const unit = meta.pack_volume_unit || 'L';
  const litresClean = String(litres).replace(/\.?0+$/, '');
  return `${qtyText} ${packNoun(meta, qty)} (${litresClean} ${unit})`;
}
